import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useRouteMatch, withRouter } from 'react-router';
import { Layout, Row, Col, Button, message, Drawer } from 'antd';
import { InvoiceFormBase, createBuyerInvoiceConfig } from '../InvoiceFormBase';
import OWPdfViewer from '../ow_pdf_viewer/OWPdfViewer';
import {
	invoicesRestCrudThunksForBuyer,
	fetchTaxLineItemsForBuyer,
	getInvoiceNumberBuyer,
} from '../../thunks/invoices_thunks';
import { workOrdersRestCrudThunksForBuyer } from '../../thunks/work_orders_thunks';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { withUserType } from '../withUserType';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { buildInvoicePayloadFromFormValues } from '../../utils/InvoiceUtils';
import { getCurrency, getEntityById, nullSafeGet } from '../../utils/DataAccessUtils';
import {
	buildProjectInvoiceEntitySelects,
	projectInvoiceEntitySelectsDispatchToProps,
	projectInvoiceEntitySelectsStateToProps,
} from '../project_invoice_entity_selects/projectInvoiceEntitySelects';
import ClearErrors from '../clear_errors/ClearErrors';
import { INVOICES_CRUD_ACTION_CREATORS } from '../../actions/invoices_actions';
import { ArrowLeftOutlined } from '@ant-design/icons';
import ResizableSplitPanel from '../common/ResizableSplitPanel';

const { Content } = Layout;

const EditBuyerInvoicePageV2: FC<any> = ({
	getInvoice,
	getWorkOrder,
	updateInvoice,
	fetchTaxLineItems,
	checkInvoiceNumber,
	userType,
	currentUser,
	updating,
	updateErrors,
	locations,
	supplierFacilities,
	fetchLocations,
	fetchMultipleLocations,
	fetchSupplierFacilities,
	fetchMultipleSupplierFacilities,
}): React.ReactElement => {
	const match = useRouteMatch<{ id: string; workOrderId?: string }>();
	const history = useHistory();
	const location = useLocation();
	const invoiceId = match.params.id;
	const workOrderIdFromRoute = match.params.workOrderId;

	const [invoice, setInvoice] = useState<any>(null);
	const [workOrder, setWorkOrder] = useState<any>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!invoiceId) return;
		setLoading(true);
		getInvoice(invoiceId)
			.then((inv) => {
				setInvoice(inv || null);
				const woId = (inv && inv.workOrderId) || workOrderIdFromRoute;
				if (woId) {
					return getWorkOrder(woId).then((wo) => {
						setWorkOrder(wo || null);
					});
				}
				setWorkOrder(null);
			})
			.catch(() => setInvoice(null))
			.finally(() => setLoading(false));
	}, [invoiceId, workOrderIdFromRoute, getInvoice, getWorkOrder]);

	const workOrderId = (invoice && invoice.workOrderId) || workOrderIdFromRoute;
	// A project invoice has no work order at all. Everything the work-order path derives
	// from one — the service provider, the billed-to location, the WO number — is a field
	// on the form instead, exactly as it is on the create page.
	const projectId = invoice && invoice.projectId;
	const isProjectInvoice = !!projectId && !workOrderId;

	const onSuccess = useCallback(
		(rec) => {
			history.replace(
				getLinkWIthBackLinkParams(
					{
						...location,
						pathname: workOrderId
							? `/${userType}/workOrders/detail/${workOrderId}/invoices`
							: projectId
							? `/${userType}/projects/detail/${projectId}/invoices`
							: `/${userType}/invoices/detail/${rec.id}`,
					},
					workOrderId ? 'Back to Work Order' : projectId ? 'Back to Project' : 'Back to Invoice',
					`/${userType}/invoices/detail/${rec.id}`
				)
			);
		},
		[history, userType, location, workOrderId, projectId]
	);

	const handleSubmit = useCallback(
		async (values, formInstance, scope) => {
			if (!invoice || (!workOrderId && !projectId)) return;

			const allFormValues = formInstance.getFieldsValue(true);
			const formValues = { ...allFormValues, ...values };

			// The same builder the create pages use, rather than a second hand-rolled copy
			// of the same payload — it already knows which anchor key to send and drops the
			// work-order fields a project invoice must not carry.
			const pickedLocation = getEntityById(formValues.locationId, locations);
			const payload = buildInvoicePayloadFromFormValues(formValues, scope, {
				...(isProjectInvoice ? { projectId } : { workOrderId }),
				locationId: formValues.locationId,
				buyerCompanyId: nullSafeGet('buyerCompanyId', invoice),
				// This page only ever updates, so a cleared optional field is sent as an
				// explicit null rather than omitted.
				isUpdate: true,
				currentUser,
				// Buyer, always: every thunk on this page is a ...ForBuyer and the route is
				// BUYER_ADMIN_ONLY. An internal tech's project invoice has no edit page yet,
				// which is why InvoiceEditButton hides the action for them.
				role: 'buyer',
				baseMeta: {
					invoicePDFLink: invoice.invoicePDFLink || undefined,
					// On a project invoice both come off the form; on a work-order invoice they
					// stay exactly where they were, which is what this page already did.
					// Explicit null when the location was cleared: the facility hangs off the
					// location, so omitting it would leave the old one behind next to a null
					// locationId — two halves of the same fact disagreeing.
					buyerFacilityId: isProjectInvoice
						? pickedLocation
							? nullSafeGet('buyerFacilityId', pickedLocation)
							: null
						: formValues.buyerFacilityId || nullSafeGet('facility.id', currentUser),
					supplierFacilityId: isProjectInvoice
						? formValues.supplierFacilityId
						: invoice && invoice.supplierFacilityId,
					isDeleted: invoice && invoice.isDeleted ? true : false,
				},
				status: formValues.status || 'pending',
			});

			try {
				const record = await updateInvoice(payload);
				if (onSuccess) onSuccess(record);
			} catch (err) {
				if (
					typeof err === 'string' &&
					err.indexOf('Invoice(s) are already associated with this work order') === 0
				) {
					message.error(err);
					history.goBack();
				}
				throw err;
			}
		},
		[
			invoice,
			workOrderId,
			projectId,
			isProjectInvoice,
			locations,
			currentUser,
			updateInvoice,
			onSuccess,
			history,
		]
	);

	const entitySelects = useMemo(
		() =>
			buildProjectInvoiceEntitySelects({
				locations,
				supplierFacilities,
				fetchLocations,
				fetchMultipleLocations,
				fetchSupplierFacilities,
				fetchMultipleSupplierFacilities,
			}),
		[
			locations,
			supplierFacilities,
			fetchLocations,
			fetchMultipleLocations,
			fetchSupplierFacilities,
			fetchMultipleSupplierFacilities,
		]
	);

	const formConfig = useMemo(() => {
		// A project invoice never has a work order, so requiring one here is what kept the
		// form from rendering for it at all.
		if (!invoice || !currentUser) return null;
		if (!workOrder && !isProjectInvoice) return null;
		return createBuyerInvoiceConfig(invoice, workOrder, currentUser, history, handleSubmit, {
			updating,
			updateErrors,
			onSuccess,
			fetchTaxLineItems,
			checkInvoiceNumber,
			...(isProjectInvoice
				? {
						hideWorkOrderNumber: true,
						showTitle: true,
						entitySelects,
						// InvoiceFormBase resolves a currency only from an explicit value or from a
						// work order, and holds the whole form on a loading placeholder until it has
						// one — so without this a project invoice never rendered at all. Same source
						// InvoiceRowDisplay uses for these invoices.
						currency: getCurrency({
							supplierFacility: nullSafeGet('supplierFacility', invoice),
							currentUser,
						}),
				  }
				: {}),
		});
	}, [
		invoice,
		workOrder,
		isProjectInvoice,
		entitySelects,
		currentUser,
		history,
		handleSubmit,
		updating,
		updateErrors,
		onSuccess,
		fetchTaxLineItems,
		checkInvoiceNumber,
	]);

	const handleBack = useCallback(() => {
		if (workOrderId) {
			history.push(`/${userType}/workOrders/detail/${workOrderId}/invoices`);
		} else if (projectId) {
			history.push(`/${userType}/projects/detail/${projectId}/invoices`);
		} else {
			history.push(`/${userType}/invoices/detail/${invoiceId}`);
		}
	}, [history, userType, workOrderId, projectId, invoiceId]);

	if (loading || !invoice) {
		return <PageLoadingPlaceholder />;
	}

	return (
		<Content>
			<Drawer
				width="100vw"
				placement="right"
				visible={true}
				style={{ top: 0 }}
				bodyStyle={{
					padding: 0,
					height: 'calc(100vh - 67px)',
					overflow: 'hidden',
				}}
				className="fullscreen-drawer"
				closeIcon={
					<Button type="ghost" icon={<ArrowLeftOutlined />} onClick={handleBack}>
						Back
					</Button>
				}
				title={<div className="h3medium">Edit Invoice</div>}
			>
				<Row style={{ height: '100%' }}>
					<ClearErrors actionCreators={INVOICES_CRUD_ACTION_CREATORS} />
					<Col span={24} style={{ height: '100%' }}>
						<ResizableSplitPanel
							leftPanel={
								<div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
									<OWPdfViewer
										file={invoice.invoicePDFLink || null}
										fileName="Invoice.pdf"
										showDelete={false}
									/>
								</div>
							}
							rightPanel={<div>{formConfig && <InvoiceFormBase config={formConfig} />}</div>}
							rightPanelClassName="flex-1 min-h-0 overflow-y-auto p-6 pt-7"
						/>
					</Col>
				</Row>
			</Drawer>
		</Content>
	);
};

const mapStateToProps = (state: any) => ({
	currentUser: state.session.currentUser,
	updating: state.invoices.updating,
	updateErrors: state.invoices.updateErrors,
	...projectInvoiceEntitySelectsStateToProps(state),
});

const mapDispatchToProps = (dispatch: any) => ({
	getInvoice: (id: string) => dispatch(invoicesRestCrudThunksForBuyer.readOne(id)),
	getWorkOrder: (id: string) => dispatch(workOrdersRestCrudThunksForBuyer.readOne(id)),
	updateInvoice: (entity: any) => dispatch(invoicesRestCrudThunksForBuyer.update(entity)),
	fetchTaxLineItems: (currencyId: string) => dispatch(fetchTaxLineItemsForBuyer(currencyId)),
	checkInvoiceNumber: (invoice: string, supplierFacilityId: string) =>
		dispatch(getInvoiceNumberBuyer(invoice, supplierFacilityId)),
	// Buyer thunks: this page is BUYER_ADMIN_ONLY.
	...projectInvoiceEntitySelectsDispatchToProps(dispatch, false),
});

export default withUserType(
	withRouter(connect(mapStateToProps, mapDispatchToProps)(EditBuyerInvoicePageV2))
);
